import * as React from "react";
import { useGetIdentity } from "@refinedev/core";
import { Link, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { useGuideT } from "@/lib/guides/useGuideT";
import { useGuideStaleSummary } from "@/lib/guides/query";
import { canAccess } from "@/providers/accessControlProvider";
import type { AdminIdentity } from "@/providers/types";

function staleCountLabel(
  count: number,
  locale: "ru" | "en",
  translate: ReturnType<typeof useGuideT>["t"],
): string {
  if (locale === "en") {
    return translate(
      count === 1
        ? "guides.sidebar.labelWithOne"
        : "guides.sidebar.labelWithMany",
      { count },
    );
  }

  const lastTwo = count % 100;
  const lastDigit = count % 10;
  const key =
    lastDigit === 1 && lastTwo !== 11
      ? "guides.sidebar.labelWithOne"
      : lastDigit >= 2 &&
          lastDigit <= 4 &&
          (lastTwo < 12 || lastTwo > 14)
        ? "guides.sidebar.labelWithFew"
        : "guides.sidebar.labelWithMany";

  return translate(key, { count });
}

export function GuidesSidebarItem(): React.JSX.Element | null {
  const location = useLocation();
  const { data: identity } = useGetIdentity<AdminIdentity>();
  const { locale, t } = useGuideT();
  const canView = canAccess({
    resource: "guides",
    action: "list",
    role: identity?.role,
  }).can;
  const summary = useGuideStaleSummary(canView);

  if (!canView) return null;

  const stalePlaces = summary.data?.places ?? 0;
  const active =
    location.pathname === "/guides" ||
    location.pathname.startsWith("/guides/");

  return (
    <li>
      <Link
        to="/guides"
        aria-label={
          stalePlaces > 0
            ? staleCountLabel(stalePlaces, locale, t)
            : t("guides.sidebar.label")
        }
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex items-center gap-[10px] px-4 min-h-[36px] py-2 text-[13px] font-[500] border-l-[3px] transition-[background,color] duration-[120ms] no-underline",
          active
            ? "bg-[var(--accent-weak)] text-[var(--accent)] border-l-[var(--accent)]"
            : "text-[var(--muted)] border-l-transparent hover:bg-[var(--surface-2)] hover:text-[var(--fg)]",
        )}
      >
        <span className="w-[18px] h-[18px] flex-shrink-0 [&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current [&>svg]:stroke-[1.8px] [&>svg]:fill-none">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v17H6.5A2.5 2.5 0 0 1 4 17.5z" />
            <path d="M4 17.5A2.5 2.5 0 0 1 6.5 15H20" />
            <path d="M8 7h8M8 10h7" />
          </svg>
        </span>
        {t("guides.sidebar.label")}
        {stalePlaces > 0 && (
          <span
            aria-hidden="true"
            className="ml-auto bg-[var(--danger)] text-white text-[10px] font-[700] px-1.5 py-px rounded-[10px]"
          >
            {stalePlaces}
          </span>
        )}
      </Link>
    </li>
  );
}
